import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, CheckCircle, XCircle, Zap } from 'lucide-react';

interface UpcomingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  attendance_count?: number;
  age_group?: 'all' | 'kids' | 'teens' | 'adults';
}

interface UpcomingSessionsCardProps {
  upcomingSessions: UpcomingSession[];
  userRole: string | undefined;
  onConfirmPromotionAttendance?: (sessionId: string, willAttend: boolean) => void;
}

const UpcomingSessionsCard: React.FC<UpcomingSessionsCardProps> = ({
  upcomingSessions,
  userRole,
  onConfirmPromotionAttendance
}) => {
  useUiLanguage();
  const formatTime = (timeString: string) => {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString(currentLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getAgeGroupBadge = (ageGroup: string | undefined) => {
    if (!ageGroup || ageGroup === 'all') return null;

    return (
      <Badge
        variant="outline"
        className={ageGroup === 'kids' ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-green-500 text-green-700 bg-green-50'}
      >
        {ageGroup === 'kids' ? uiText("Kids (5-12)") : ageGroup === 'teens' ? uiText("Teens (13-17)") : uiText("Adults (18+)")}
      </Badge>
    );
  };

  if (!upcomingSessions || upcomingSessions.length === 0) {
    return null;
  }

  return (
    <Card className="bg-white dark:bg-gray-800 shadow-sm">
      <CardHeader className="pb-3 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            {uiText("Upcoming Sessions")}</CardTitle>
          <Badge variant="secondary" className="text-xs">
            {upcomingSessions.length} {' '}{uiText("session")}{upcomingSessions.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-4 sm:px-6">
        <div className="space-y-4">
          {upcomingSessions.map((session) => (
            <div key={session.id} className="group hover:shadow-md transition-all duration-200 p-4 border rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 hover:from-red-50 hover:to-red-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-base">{session.title}</h4>
                    <Badge variant="default" className="bg-red-600 hover:bg-red-700">{uiText("Training")}</Badge>
                    {getAgeGroupBadge(session.age_group)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-red-600" />
                      <span className="font-medium">{formatDateToDDMMYYYY(session.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-red-600" />
                      <span>
                        {formatTime(session.start_time)} - {formatTime(session.end_time)}
                      </span>
                    </div>
                    {session.max_participants && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-red-600" />
                        <span>
                          {session.attendance_count || 0}/{session.max_participants} {' '}{uiText("participants")}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default UpcomingSessionsCard;